import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { Paper, Typography } from '@material-ui/core'
import { Notes as NotesIcon } from '@material-ui/icons'

const MIN_HEIGHT = 120
const MAX_HEIGHT = 600
const DEFAULT_HEIGHT = 260

const ScratchpadDrawer = ({ url }) => {
  const [openDrawer, setOpenDrawer] = React.useState(false)
  const [height, setHeight] = React.useState(DEFAULT_HEIGHT)
  const [body, setBody] = React.useState('')
  const [loaded, setLoaded] = React.useState(false)
  const [savedAt, setSavedAt] = React.useState(null)
  const saveTimer = React.useRef(null)

  // Load the scratchpad the first time the drawer is opened.
  React.useEffect(() => {
    if (!openDrawer || loaded) return
    axios.get(`${url}/api/quick-capture/scratchpad`).then(res => {
      setBody(res.data.body || '')
      setLoaded(true)
    }).catch(err => {
      console.error('Failed to load scratchpad:', err)
      setLoaded(true)
    })
  }, [openDrawer, loaded, url])

  // Debounced auto-save.
  const scheduleSave = (value) => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      axios.put(`${url}/api/quick-capture/scratchpad`, { body: value })
        .then(() => setSavedAt(new Date()))
        .catch(err => console.error('Scratchpad auto-save failed:', err))
    }, 800)
  }

  const onChange = (e) => {
    const value = e.target.value
    setBody(value)
    scheduleSave(value)
  }

  React.useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current) }, [])

  // Drag the top edge to resize.
  const onResizeStart = (e) => {
    const startY = e.clientY
    const startH = height
    const onMove = (ev) => {
      const next = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startH + (startY - ev.clientY)))
      setHeight(next)
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  return (
    <>
      {/* Always-visible grab tab at the bottom edge */}
      {!openDrawer && (
        <div
          onClick={() => setOpenDrawer(true)}
          style={{
            position: 'fixed', left: '50%', bottom: 0, transform: 'translateX(-50%)',
            zIndex: 1400, background: '#fffde7', border: '1px solid #f0e6a8', borderBottom: 'none',
            borderRadius: '8px 8px 0 0', padding: '4px 16px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 -2px 8px rgba(0,0,0,0.12)',
          }}
        >
          <NotesIcon fontSize="small" style={{ color: '#bfae4e' }} />
          <Typography variant="body2" style={{ fontWeight: 600 }}>Scratchpad</Typography>
        </div>
      )}

      {openDrawer && (
        <Paper
          elevation={8}
          style={{
            position: 'fixed', left: 0, right: 0, bottom: 0, height, zIndex: 1400,
            display: 'flex', flexDirection: 'column',
          }}
        >
          {/* Resize handle / header */}
          <div
            onPointerDown={onResizeStart}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '4px 12px', background: '#fffde7', borderBottom: '1px solid #f0e6a8',
              cursor: 'ns-resize', userSelect: 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <NotesIcon fontSize="small" style={{ color: '#bfae4e' }} />
              <Typography variant="body2" style={{ fontWeight: 600 }}>Scratchpad</Typography>
              {savedAt && (
                <Typography variant="caption" color="textSecondary">
                  saved {savedAt.toLocaleTimeString()}
                </Typography>
              )}
            </div>
            <Typography
              variant="body2"
              style={{ cursor: 'pointer', fontWeight: 600 }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => setOpenDrawer(false)}
            >
              Close
            </Typography>
          </div>

          <textarea
            value={body}
            onChange={onChange}
            placeholder="Loose running notes — auto-saved."
            style={{
              flex: 1, border: 'none', resize: 'none', outline: 'none',
              padding: 12, fontSize: 14, fontFamily: 'inherit', lineHeight: 1.5,
            }}
          />
        </Paper>
      )}
    </>
  )
}

const mapStateToProps = (state) => ({ url: state.url })
export default connect(mapStateToProps)(ScratchpadDrawer)
